import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useRetainedState } from "@appretio/shared";
import { type Dispatch, type SetStateAction, useEffect, useMemo } from "react";
import {
  type AvailableContextOptions,
  pruneStaleContextSelections
} from "../utils/pruneStaleContextSelections";
import type { useContextOptions } from "./useContextWithSync";
import type { CubeProducer } from "./useForecastWorkspace";

/**
 * The cube pages, each with its own retained selections. A brand picked on
 * forecast stays on forecast: the page remembers it across navigation, and
 * the other pages open on their own remembered selections — or, on a fresh
 * session, on the default context view.
 */
export type CubePage =
  | "forecast"
  | "budget"
  | "forecast-summary"
  | "budget-summary";

/**
 * The temporal-grid page a producer is planned on. Also that page's
 * saved-view list: the two share a name.
 */
export function pageForProducer(
  producer: CubeProducer
): Extract<CubePage, "forecast" | "budget"> {
  return producer === "budget" ? "budget" : "forecast";
}

/** The retained-state scope everything a page remembers lives under. */
export function pageScope(page: CubePage): string {
  return `cube:${page}`;
}

/**
 * Context-dimension and store-status selections of one cube page. Retained
 * per page: they survive navigating away and back, and reset on reload.
 */
export function useCubeSelectionState(page: CubePage) {
  const scope = pageScope(page);
  const [selectedContextOptions, setSelectedContextOptions] =
    useRetainedState<ForecastContextSelectedOptions>(
      scope,
      "contextOptions",
      {}
    );
  const [selectedStoreStatuses, setSelectedStoreStatuses] = useRetainedState<
    string[]
  >(scope, "storeStatuses", []);

  return {
    selectedContextOptions,
    setSelectedContextOptions,
    selectedStoreStatuses,
    setSelectedStoreStatuses
  };
}

/** The selections the Context picker saves and restores. */
export type CubeSelectionState = ReturnType<typeof useCubeSelectionState>;

/**
 * Prunes retained context selections down to what the dropdowns can
 * actually offer, so a value that left with its cube (e.g. a brand that
 * only existed in a user cube the user just cleared) stops filtering the
 * data read and stops lingering as a trigger chip.
 *
 * Validated against the fetched lists rather than diffing the cube ids:
 * selections are retained across navigation, so cubes can change on
 * another cube page while this one is unmounted. Mirrors
 * ``useClearStaleBudgetVersion``.
 */
export function usePruneStaleContextSelections(
  context: Pick<
    ReturnType<typeof useContextOptions>,
    "baseContextOptions" | "mergedFilteredContextOptions" | "baseLoading"
  >,
  setSelectedContextOptions: Dispatch<
    SetStateAction<ForecastContextSelectedOptions>
  >
) {
  const { baseContextOptions, mergedFilteredContextOptions, baseLoading } =
    context;

  // The union is what ContextDropdown renders (the filtered values, then
  // the base ones they don't cover), so pruning against anything
  // narrower could drop a value the user can still see and pick.
  const availableByField = useMemo(() => {
    const available: AvailableContextOptions = {};
    for (const source of [baseContextOptions, mergedFilteredContextOptions]) {
      for (const [field, values] of Object.entries(source ?? {})) {
        if (!values?.length) continue;
        const known = available[field];
        available[field] = known?.length
          ? [...new Set([...known, ...values])]
          : values;
      }
    }
    return available;
  }, [baseContextOptions, mergedFilteredContextOptions]);

  useEffect(() => {
    // Nothing is provably stale while the wide catalogue is unknown.
    if (baseLoading) return;
    setSelectedContextOptions((prev) =>
      pruneStaleContextSelections(prev, availableByField)
    );
  }, [availableByField, baseLoading, setSelectedContextOptions]);
}
